import { isArray, mergeWith } from 'lodash-es';

import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { MwAuthService } from '../../auth/services/auth.service';
import { MwCoreDateTimeService } from '../../services/date-time.service';
import { RequestOptions } from '../models';
import { AggHttpService } from './agg-http.service';
import { MwHttpBaseService } from './http-base.service';
import { MwHttpErrorHandlerOptions } from './http-error-handler.service';

@Injectable()
export class MwHttpService extends MwHttpBaseService {
  constructor(
    public override aggService: AggHttpService,
    public authService: MwAuthService,
    public dateService: MwCoreDateTimeService
  ) {
    super(aggService);
  }

  rawAuthRequest<T>(
    method: string,
    url: string,
    options?: RequestOptions
  ): Observable<T> {
    return this.authService.getAccessToken(options?.authConfigId).pipe(
      take(1),
      switchMap((accessToken) => {
        const errorHandlerOptions = this.getErrorHandlerOptions(options);
        const headers = this.getHeaders(
          accessToken,
          method,
          options?.body,
          options?.companyId
        );

        const httpOptions = {
          headers,
          body: options && options.body,
          responseType: options && options.responseType,
        };

        return this.rawRequest<T>(
          method,
          url,
          httpOptions,
          errorHandlerOptions
        );
      })
    );
  }

  authGet<T>(url: string, options?: RequestOptions): Observable<T> {
    return this.rawAuthRequest('GET', url, options);
  }

  authPost<T>(url: string, options?: RequestOptions): Observable<T> {
    return this.rawAuthRequest('POST', url, options);
  }

  authPut<T>(url: string, options?: RequestOptions): Observable<T> {
    return this.rawAuthRequest('PUT', url, options);
  }

  authPatch<T>(url: string, options?: RequestOptions): Observable<T> {
    return this.rawAuthRequest('PATCH', url, options);
  }

  authDelete<T>(url: string, options?: RequestOptions): Observable<T> {
    return this.rawAuthRequest('DELETE', url, options);
  }

  protected getHeaderItems(
    accessToken: string,
    companyId?: number
  ): Record<string, string> {
    let headers: Record<string, string> = {};
    const olsonTimeZoneId = this.dateService.getLocalOlsonTimeZone();
    const currentLanguage = this.aggService.translateService.currentLang;
    const clientId =
      this.aggService.configService.getInstantConfig()?.environment?.clientId;

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    if (olsonTimeZoneId) {
      headers['tz'] = olsonTimeZoneId;
    }

    if (currentLanguage) {
      headers['mw-language'] = currentLanguage;
    }

    if (clientId) {
      headers['mw-client-id'] = clientId;
    }

    if (this.httpConfig && this.httpConfig.customHeaders) {
      this.httpConfig.customHeaders.forEach((t) => {
        t.value.pipe(take(1)).subscribe((rawValue) =>
          Object.assign(headers, {
            [t.name]: String(rawValue),
          })
        );
      });
    }

    // If companyId is specified, override mw-company-id to make query to a different company than the logged in company.
    // Example, setup integration on a different Company the user has access to.
    if (companyId) {
      headers = Object.assign({}, headers, {
        ['mw-company-id']: String(companyId),
      });
    }

    return headers;
  }

  private getHeaders(
    accessToken: string,
    method: string,
    body: any,
    companyId?: number
  ): HttpHeaders {
    let headerItems = this.getHeaderItems(accessToken, companyId);
    headerItems = Object.assign(
      {},
      {
        Accept: 'application/json',
      },
      headerItems
    );

    if (method !== 'GET' && !(body instanceof FormData)) {
      headerItems = Object.assign(
        {},
        {
          ['Content-Type']: 'application/json',
        },
        headerItems
      );
    }

    return new HttpHeaders(headerItems);
  }

  private getErrorHandlerOptions(
    requestOptions?: RequestOptions,
    additionalRequestOptions?: RequestOptions
  ): MwHttpErrorHandlerOptions {
    let errorHandlerOptions: MwHttpErrorHandlerOptions = {
      interpolateParams: requestOptions?.interpolateParamsForError || {},
      ignoreNotifyErrorCodes: requestOptions?.ignoreNotifyErrorCodes || [],
      ignoreNotifyStatusCodes: requestOptions?.ignoreNotifyStatusCodes || [],
    };

    if (additionalRequestOptions) {
      errorHandlerOptions = mergeWith(
        errorHandlerOptions,
        additionalRequestOptions,
        (objValue: any, srcValue: any) => {
          if (isArray(objValue) && isArray(srcValue)) {
            return objValue.concat(srcValue);
          }

          return objValue;
        }
      );
    }

    return errorHandlerOptions;
  }
}
